import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  Dot,
  Lightbulb,
  MessageSquareText,
  Minus,
  RefreshCcw,
  Target,
  Trophy,
} from 'lucide-react';

import { AppDialog } from 'components/ui/app-dialog';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { ROLEPLAY_EXPRESSIONS, RoleplayPersonaPortrait } from 'components/roleplay/RoleplayPersonaPortrait';
import type {
  RoleplayExpression,
  RoleplayScenario,
  RoleplayScorecard,
  RoleplayScorecardTurnMark,
  RoleplayTurn,
} from 'services/api/roleplayClient';
import { ScorecardIcon } from './scorecardIcons';
import {
  SCORE_COLORS,
  TIER_LABELS,
  buildScorecardView,
  type ResolvedMoment,
  type ScorecardCategoryView,
  type ScoreTone,
} from './scorecardModel';

export type ScorecardAction = {
  label: string;
  onClick: () => void;
  testId: string;
  disabled?: boolean;
};

type Persona = RoleplayScenario['persona'];

const DESKTOP_QUERY = '(min-width: 1024px)';
const SKILL_STATE_KEY = 'roleplayScorecardSkill';

const useDesktopLayout = () => {
  const query = () => typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(DESKTOP_QUERY).matches;
  const [isDesktop, setIsDesktop] = useState<boolean>(query);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(DESKTOP_QUERY);
    const listener = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    list.addEventListener?.('change', listener);
    setIsDesktop(list.matches);
    return () => list.removeEventListener?.('change', listener);
  }, []);
  return isDesktop;
};

/** Eased 0→1 count-up for the reveal; instant under reduced motion or without rAF. */
const useRevealProgress = (durationMs = 1100) => {
  const [progress, setProgress] = useState(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return 1;
    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return reducedMotion ? 1 : 0;
  });
  const animate = progress < 1;
  useEffect(() => {
    if (!animate) return undefined;
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, Math.max(0, (now - start) / durationMs));
      setProgress(1 - (1 - k) ** 3);
      if (k < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
    // Run once on reveal; later renders keep the finished value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return progress;
};

const expressionLabel = (expression: RoleplayExpression) =>
  (ROLEPLAY_EXPRESSIONS.find((item) => item.value === expression)?.label ?? expression).toLowerCase();

const PersonaAvatar: React.FC<{
  persona: Persona;
  expression: RoleplayExpression | null;
  className: string;
}> = ({ persona, expression, className }) => (
  <div className={`shrink-0 overflow-hidden rounded-full bg-white ${className}`}>
    <RoleplayPersonaPortrait
      persona={persona}
      expression={expression ?? persona.starting_expression}
      className="h-full w-full"
      showTestId={false}
    />
  </div>
);

const ScoreRing: React.FC<{
  score: number;
  display: number;
  tone: ScoreTone;
  size: number;
  thickness: number;
  children: React.ReactNode;
  className?: string;
}> = ({ score, display, tone, size, thickness, children, className = '' }) => (
  <div
    className={`relative flex shrink-0 items-center justify-center rounded-full ${className}`}
    style={{
      width: size,
      height: size,
      background: `conic-gradient(${SCORE_COLORS[tone]} 0 ${display}%, rgba(255,255,255,0.7) 0)`,
    }}
    data-score={score}
  >
    <div
      className="flex flex-col items-center justify-center rounded-full bg-white"
      style={{ width: size - thickness * 2, height: size - thickness * 2 }}
    >
      {children}
    </div>
  </div>
);

const ScoreBar: React.FC<{ category: ScorecardCategoryView; progress: number; thin?: boolean }> = ({
  category,
  progress,
  thin = false,
}) => (
  <div className={`relative rounded-full bg-[var(--rp-score-track)] ${thin ? 'h-1.5' : 'h-2'}`} aria-hidden="true">
    <div
      className="absolute inset-y-0 left-0 rounded-full"
      style={{ width: `${category.score * progress}%`, background: SCORE_COLORS[category.tone] }}
    />
    {category.last_score !== null ? (
      <div
        className="absolute -bottom-[3px] -top-[3px] w-0.5 rounded-sm bg-[var(--rp-score-ink)]"
        style={{ left: `${category.last_score}%` }}
        data-testid={`roleplay-scorecard-last-${category.key}`}
      />
    ) : null}
  </div>
);

const LastTryLegend: React.FC = () => (
  <span className="flex items-center gap-1.5 text-xs text-foundation-base-darkbase-dark-400">
    <span className="h-2.5 w-0.5 rounded-sm bg-[var(--rp-score-ink)]" aria-hidden="true" />
    last try
  </span>
);

const DeltaPill: React.FC<{ delta: number | null }> = ({ delta }) => {
  if (delta === null) return null;
  const up = delta >= 0;
  return (
    <span
      className="rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{
        background: up ? 'var(--rp-score-strong-soft)' : 'var(--rp-score-miss-soft)',
        color: up ? 'var(--rp-score-strong)' : 'var(--rp-score-focus)',
      }}
      data-testid="roleplay-scorecard-delta"
    >
      {delta > 0 ? '+' : ''}{delta} vs last
    </span>
  );
};

const MARK_STYLE: Record<RoleplayScorecardTurnMark, {
  background: string;
  color: string;
  Icon: typeof Check;
  label: string;
}> = {
  hit: { background: 'var(--rp-score-strong-soft)', color: 'var(--rp-score-strong)', Icon: Check, label: 'nailed it' },
  miss: { background: 'var(--rp-score-miss-soft)', color: 'var(--rp-score-solid)', Icon: Minus, label: 'missed chance' },
  none: { background: 'var(--rp-score-none-soft)', color: 'var(--rp-score-none)', Icon: Dot, label: 'not relevant' },
};

const MarkLegend: React.FC<{ showMoment?: boolean }> = ({ showMoment = false }) => (
  <div className="flex flex-wrap gap-3.5 text-xs text-foundation-base-darkbase-dark-400">
    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[var(--rp-score-strong)]" aria-hidden="true" />Nailed it</span>
    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[var(--rp-score-solid)]" aria-hidden="true" />Missed chance</span>
    {showMoment ? (
      <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full border-2 border-[var(--rp-score-ink)]" aria-hidden="true" />Shown below</span>
    ) : null}
  </div>
);

const TurnMarks: React.FC<{ category: ScorecardCategoryView }> = ({ category }) => (
  <ol className="grid grid-cols-6 gap-1.5" data-testid="roleplay-scorecard-marks">
    {category.turn_marks.map(({ learner_turn: turn, mark }) => {
      const style = MARK_STYLE[mark] ?? MARK_STYLE.none;
      const isMoment = turn === category.moment.learner_turn;
      return (
        <li
          key={turn}
          className="flex flex-col items-center gap-1"
          aria-label={`Turn ${turn}: ${style.label}${isMoment ? ', shown below' : ''}`}
          data-mark={mark}
        >
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full"
            style={{
              background: style.background,
              color: style.color,
              boxShadow: isMoment ? '0 0 0 2px #fff, 0 0 0 4px var(--rp-score-ink)' : 'none',
            }}
            aria-hidden="true"
          >
            <style.Icon size={14} />
          </span>
          <span className="text-xs text-foundation-base-darkbase-dark-400" aria-hidden="true">T{turn}</span>
        </li>
      );
    })}
  </ol>
);

const MomentExchange: React.FC<{
  moment: ResolvedMoment;
  persona: Persona;
  personaBubbleClass: string;
}> = ({ moment, persona, personaBubbleClass }) => (
  <>
    <p
      className="type-body-s max-w-[85%] self-end rounded-[16px_16px_4px_16px] bg-foundation-base-darkbase-dark-500 px-3.5 py-2.5 text-white"
      data-testid="roleplay-scorecard-moment-learner"
    >
      {moment.learnerText}
    </p>
    {moment.personaText ? (
      <div className="flex items-end gap-2">
        <PersonaAvatar persona={persona} expression={moment.personaExpression} className="h-9 w-9 shadow-[0_0_0_1px_rgba(235,235,237,1)]" />
        <div className={`type-body-s max-w-[78%] rounded-[16px_16px_16px_4px] px-3.5 py-2.5 text-foundation-base-darkbase-dark-500 ${personaBubbleClass}`}>
          <p>{moment.personaText}</p>
          {moment.personaExpression ? (
            <p className="text-xs text-foundation-base-darkbase-dark-400">
              {persona.name} felt {expressionLabel(moment.personaExpression)}
            </p>
          ) : null}
        </div>
      </div>
    ) : null}
  </>
);

const Takeaways: React.FC<{ category: ScorecardCategoryView; tinted?: boolean }> = ({ category, tinted = false }) => (
  <>
    {category.worked ? (
      <section
        className="rounded-xl px-4 py-3"
        style={{ background: tinted ? 'var(--rp-score-strong-soft)' : 'rgba(255,255,255,0.6)' }}
        data-testid="roleplay-scorecard-worked"
      >
        <p className="type-body-s-semibold mb-2 text-foundation-base-darkbase-dark-700">What worked</p>
        <p className="flex items-start gap-2 text-sm text-foundation-base-darkbase-dark-500">
          <CheckCircle2 size={14} aria-hidden="true" className="mt-1 shrink-0" />
          <span>{category.worked}</span>
        </p>
      </section>
    ) : null}
    {category.next ? (
      <section className="rounded-xl bg-foundation-orange-50 px-4 py-3" data-testid="roleplay-scorecard-next">
        <p className="type-body-s-semibold mb-2 text-foundation-base-darkbase-dark-700">Try next time</p>
        <p className="flex items-start gap-2 text-sm text-foundation-base-darkbase-dark-500">
          <ArrowRight size={14} aria-hidden="true" className="mt-1 shrink-0" />
          <span>{category.next}</span>
        </p>
      </section>
    ) : null}
  </>
);

const HighlightCards: React.FC<{
  best: ScorecardCategoryView | null;
  focus: ScorecardCategoryView | null;
  onOpen: (key: string) => void;
}> = ({ best, focus, onOpen }) => {
  if (!best && !focus) return null;
  return (
    <div className={`grid gap-2.5 ${best && focus ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {best ? (
        <button
          type="button"
          onClick={() => onOpen(best.key)}
          className="flex flex-col gap-1.5 rounded-2xl bg-[var(--rp-score-strong-soft)] px-3.5 py-3 text-left transition-colors hover:bg-[var(--rp-score-strong-soft-hover)]"
          data-testid="roleplay-scorecard-best"
        >
          <span className="flex items-center gap-1 text-xs font-semibold text-[var(--rp-score-strong)]"><Trophy size={14} aria-hidden="true" />Best move</span>
          <span className="type-body-s-semibold text-foundation-base-darkbase-dark-700">{best.label}</span>
          <span className="text-xs text-foundation-base-darkbase-dark-400">Turn {best.moment.learner_turn} · {best.score}</span>
        </button>
      ) : null}
      {focus ? (
        <button
          type="button"
          onClick={() => onOpen(focus.key)}
          className="flex flex-col gap-1.5 rounded-2xl bg-foundation-orange-50 px-3.5 py-3 text-left transition-colors hover:bg-foundation-orange-100"
          data-testid="roleplay-scorecard-focus"
        >
          <span className="flex items-center gap-1 text-xs font-semibold text-foundation-orange-500"><Target size={14} aria-hidden="true" />Focus next</span>
          <span className="type-body-s-semibold text-foundation-base-darkbase-dark-700">{focus.label}</span>
          <span className="text-xs text-foundation-base-darkbase-dark-400">Turn {focus.moment.learner_turn} · {focus.score}</span>
        </button>
      ) : null}
    </div>
  );
};

const TurningPointCard: React.FC<{
  persona: Persona;
  turn: number;
  text: string;
  expression: RoleplayExpression | null;
  onOpen: () => void;
  showChevron: boolean;
}> = ({ persona, turn, text, expression, onOpen, showChevron }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3.5 text-left shadow-elevation-sm transition-colors hover:bg-white/85"
    data-testid="roleplay-scorecard-turning-point"
  >
    <PersonaAvatar persona={persona} expression={expression} className="h-11 w-11 shadow-[0_0_0_2px_rgba(255,236,232,1)]" />
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="text-xs font-semibold text-foundation-orange-500">Turning point · Turn {turn}</span>
      <span className="type-body-s text-foundation-base-darkbase-dark-500">{text}</span>
    </span>
    {showChevron ? <ChevronRight size={18} aria-hidden="true" className="mt-3 shrink-0 text-foundation-base-darkbase-dark-300" /> : null}
  </button>
);

const OverallCard: React.FC<{
  persona: Persona;
  endExpression: RoleplayExpression;
  overallScore: number;
  overallTone: ScoreTone;
  overallTier: string;
  headline: string;
  summary: string;
  successful: boolean;
  progress: number;
  large: boolean;
}> = ({ persona, endExpression, overallScore, overallTone, overallTier, headline, summary, successful, progress, large }) => (
  <Card variant="glass" padding="compact" disableAnimation maxWidth="none" data-testid="roleplay-scorecard-overall">
    <div className={`flex flex-col items-center text-center ${large ? 'gap-3.5 pb-1 pt-3' : 'gap-3 pb-1 pt-2'}`}>
      <div className="relative" role="img" aria-label={`Overall score ${overallScore} out of 100, ${overallTier}`}>
        <ScoreRing score={overallScore} display={overallScore * progress} tone={overallTone} size={large ? 176 : 160} thickness={13}>
          <span className="type-heading-h1 text-foundation-base-darkbase-dark-700" style={{ fontSize: large ? 56 : 52, lineHeight: large ? '60px' : '56px' }} aria-hidden="true">
            {Math.round(overallScore * progress)}
          </span>
          <span className="type-body-s-semibold" style={{ color: SCORE_COLORS[overallTone] }} aria-hidden="true">{overallTier}</span>
        </ScoreRing>
        <PersonaAvatar
          persona={persona}
          expression={endExpression}
          className={`absolute border-[3px] border-white shadow-elevation-sm ${large ? '-right-1.5 bottom-1 h-[60px] w-[60px]' : '-right-2 bottom-0.5 h-14 w-14'}`}
        />
      </div>
      <div className="flex flex-col gap-1">
        {headline ? <h2 className="type-heading-h4 text-foundation-base-darkbase-dark-700">{headline}</h2> : null}
        {summary ? <p className="type-body-s text-foundation-base-darkbase-dark-500">{summary}</p> : null}
      </div>
      <span
        className="type-body-s-semibold flex items-center gap-1 rounded-full px-2.5 py-1 text-foundation-base-darkbase-dark-700"
        style={{ background: successful ? 'var(--rp-score-strong-soft)' : 'var(--rp-score-miss-soft)' }}
        data-testid="roleplay-outcome"
      >
        {successful ? <CheckCircle2 size={14} aria-hidden="true" /> : <Lightbulb size={14} aria-hidden="true" />}
        {successful ? 'Goal met' : 'Goal not met yet'}
      </span>
    </div>
  </Card>
);

const ConversationDialog: React.FC<{
  persona: Persona;
  turns: RoleplayTurn[];
  onClose: () => void;
}> = ({ persona, turns, onClose }) => (
  <AppDialog
    onClose={onClose}
    title="Full conversation"
    description={`Everything you and ${persona.name} said in this practice.`}
    size="sm"
    data-testid="roleplay-scorecard-conversation"
    actions={<Button onClick={onClose} size="auto">Back to summary</Button>}
  >
    <div className="flex flex-col gap-2.5">
      {turns.map((turn) => (turn.speaker === 'learner' ? (
        <p key={turn.id} className="type-body-s max-w-[85%] self-end rounded-[16px_16px_4px_16px] bg-foundation-base-darkbase-dark-500 px-3.5 py-2.5 text-white">
          {turn.text}
        </p>
      ) : (
        <div key={turn.id} className="flex items-end gap-2">
          <PersonaAvatar persona={persona} expression={turn.persona_expression} className="h-8 w-8" />
          <p className="type-body-s max-w-[78%] rounded-[16px_16px_16px_4px] bg-foundation-calm-blue-50 px-3.5 py-2.5 text-foundation-base-darkbase-dark-500">
            <span className="sr-only">{persona.name}: </span>{turn.text}
          </p>
        </div>
      )))}
    </div>
  </AppDialog>
);

export const ScorecardDebrief: React.FC<{
  scenario: RoleplayScenario;
  turns: RoleplayTurn[];
  endExpression: RoleplayExpression;
  scorecard: RoleplayScorecard;
  summary: string;
  successful: boolean;
  primaryAction: ScorecardAction;
  secondaryAction: ScorecardAction;
  onPracticeSkill: (category: { key: string; label: string; next: string }) => void;
  practiceDisabled: boolean;
  onBack?: () => void;
}> = ({
  scenario,
  turns,
  endExpression,
  scorecard,
  summary,
  successful,
  primaryAction,
  secondaryAction,
  onPracticeSkill,
  practiceDisabled,
  onBack,
}) => {
  const isDesktop = useDesktopLayout();
  const progress = useRevealProgress();
  const conversation = useMemo(
    () => turns.filter((turn) => !turn.metadata?.safety_only),
    [turns],
  );
  const view = useMemo(() => buildScorecardView(scorecard, conversation), [scorecard, conversation]);
  const persona = scenario.persona;
  const count = view.categories.length;
  // The mobile skill detail is a history entry (same URL, router state), so the
  // browser/OS Back button returns to the summary instead of leaving practice.
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = (location.state && typeof location.state === 'object' ? location.state : {}) as Record<string, unknown>;
  const openKey = typeof routeState[SKILL_STATE_KEY] === 'string' ? routeState[SKILL_STATE_KEY] as string : null;
  const routedIndex = openKey === null ? -1 : view.categories.findIndex((category) => category.key === openKey);
  const openIndex = !isDesktop && routedIndex >= 0 ? routedIndex : null;
  const showSkill = (index: number, { replace = false } = {}) => navigate(
    { pathname: location.pathname, search: location.search, hash: location.hash },
    { replace, state: { ...routeState, [SKILL_STATE_KEY]: view.categories[index].key } },
  );
  const closeSkill = () => navigate(-1);
  const [selectedIndex, setSelectedIndex] = useState(view.lowestIndex);
  const [showConversation, setShowConversation] = useState(false);
  const detailHeadingRef = useRef<HTMLHeadingElement>(null);
  const skillRowRefs = useRef(new Map<string, HTMLButtonElement>());
  const lastOpenedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (openKey !== null && openIndex !== null) {
      lastOpenedKeyRef.current = openKey;
      detailHeadingRef.current?.focus();
      return;
    }
    // Back on the summary: return focus (and the scroll position) to the row
    // the learner opened.
    const key = lastOpenedKeyRef.current;
    lastOpenedKeyRef.current = null;
    if (key) skillRowRefs.current.get(key)?.focus();
  }, [openIndex, openKey]);

  const best = view.bestIndex === null ? null : view.categories[view.bestIndex];
  const focus = view.focusIndex === null ? null : view.categories[view.focusIndex];
  const overallTier = successful ? TIER_LABELS[view.overallTone] : 'Not yet';
  const openCategory = (key: string) => {
    const index = view.categories.findIndex((category) => category.key === key);
    if (index < 0) return;
    if (isDesktop) setSelectedIndex(index);
    else showSkill(index);
  };
  const practiceSkill = (category: ScorecardCategoryView) => {
    if (openKey !== null) {
      // Leave no open skill in history for the next attempt's scorecard.
      const { [SKILL_STATE_KEY]: _open, ...rest } = routeState;
      navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: rest });
    }
    onPracticeSkill({ key: category.key, label: category.label, next: category.next });
  };

  const actionButtons = (
    <>
      <Button onClick={primaryAction.onClick} disabled={primaryAction.disabled} data-testid={primaryAction.testId}>
        {primaryAction.label}
      </Button>
      <Button
        variant="secondary"
        onClick={secondaryAction.onClick}
        disabled={secondaryAction.disabled}
        data-testid={secondaryAction.testId}
      >
        {secondaryAction.label}
      </Button>
    </>
  );
  const conversationButton = (layout: 'stack' | 'inline') => (
    <Button
      variant="ghost"
      size={layout === 'inline' ? 'auto' : undefined}
      onClick={() => setShowConversation(true)}
      data-testid="roleplay-scorecard-conversation-open"
    >
      <MessageSquareText size={16} aria-hidden="true" />
      {layout === 'inline' ? 'Full conversation' : 'Read the full conversation'}
    </Button>
  );

  const eyebrow = (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-semibold tracking-[0.04em] text-foundation-base-darkbase-dark-400">PRACTICE SUMMARY</span>
      <span className={`${isDesktop ? 'type-body-m-semibold' : 'type-body-s-semibold'} text-foundation-base-darkbase-dark-700`}>{scenario.title}</span>
    </div>
  );

  const overallCard = (
    <OverallCard
      persona={persona}
      endExpression={endExpression}
      overallScore={view.overallScore}
      overallTone={view.overallTone}
      overallTier={overallTier}
      headline={view.headline}
      summary={summary}
      successful={successful}
      progress={progress}
      large={isDesktop}
    />
  );

  const turningPoint = !successful && view.turningPoint ? (
    <TurningPointCard
      persona={persona}
      turn={view.turningPoint.moment?.learnerTurn ?? view.categories[view.turningPoint.index].moment.learner_turn}
      text={view.turningPoint.text}
      expression={view.turningPoint.moment?.personaExpression ?? null}
      onOpen={() => openCategory(view.categories[view.turningPoint!.index].key)}
      showChevron={!isDesktop}
    />
  ) : null;

  const conversationDialog = showConversation ? (
    <ConversationDialog persona={persona} turns={conversation} onClose={() => setShowConversation(false)} />
  ) : null;

  if (isDesktop) {
    const selected = view.categories[selectedIndex] ?? view.categories[view.lowestIndex];
    return (
      <div className="rp-scorecard flex w-full max-w-[80rem] flex-col gap-5 px-8 pb-8 pt-6" data-testid="roleplay-results" data-layout="desktop">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            {onBack ? (
              <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back" data-testid="roleplay-back">
                <ArrowLeft size={18} aria-hidden="true" />
              </Button>
            ) : null}
            {eyebrow}
          </div>
          <div className="flex gap-2.5">
            {conversationButton('inline')}
            <Button
              variant="secondary"
              size="auto"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
              data-testid={secondaryAction.testId}
            >
              {secondaryAction.label}
            </Button>
            <Button size="auto" onClick={primaryAction.onClick} disabled={primaryAction.disabled} data-testid={primaryAction.testId}>
              {primaryAction.label}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-[340px_minmax(0,1fr)] items-start gap-6">
          <div className="flex flex-col gap-3.5">
            {overallCard}
            {turningPoint}
            <HighlightCards best={best} focus={focus} onOpen={openCategory} />
          </div>

          <section className="flex flex-col gap-4 rounded-3xl bg-white/60 p-5 shadow-elevation-sm" aria-label="Your skills">
            <div className="flex items-center justify-between">
              <h2 className="type-body-m-semibold text-foundation-base-darkbase-dark-700">Your skills</h2>
              {view.hasLastTry ? <LastTryLegend /> : null}
            </div>
            <div
              className="grid gap-2.5"
              style={{ gridTemplateColumns: `repeat(${Math.min(count, 4)}, minmax(0, 1fr))` }}
              role="tablist"
              aria-label="Skills"
            >
              {view.categories.map((category, index) => {
                const isSelected = index === selectedIndex;
                return (
                  <button
                    key={category.key}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => setSelectedIndex(index)}
                    className="flex flex-col gap-2.5 rounded-2xl p-3.5 text-left transition-colors hover:bg-white"
                    style={{
                      background: isSelected ? '#fff' : 'rgba(255,255,255,0.6)',
                      boxShadow: isSelected ? '0 0 0 2px var(--rp-score-ink)' : 'none',
                    }}
                    data-testid={`roleplay-scorecard-skill-${category.key}`}
                    data-score={category.score}
                  >
                    <span className="flex items-center justify-between">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-foundation-calm-blue-50 text-foundation-base-darkbase-dark-500">
                        <ScorecardIcon name={category.icon} size={16} />
                      </span>
                      <span className="type-heading-h4 text-foundation-base-darkbase-dark-700">{Math.round(category.score * progress)}</span>
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="type-body-s-semibold text-foundation-base-darkbase-dark-700">{category.label}</span>
                      <span className="text-xs font-semibold" style={{ color: SCORE_COLORS[category.tone] }}>{category.tier}</span>
                    </span>
                    <ScoreBar category={category} progress={progress} thin />
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-4 pt-1" role="tabpanel" data-testid="roleplay-scorecard-detail">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="type-heading-h5 text-foundation-base-darkbase-dark-700">{selected.label}</h3>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold" style={{ color: SCORE_COLORS[selected.tone] }}>
                      {selected.tier} · {selected.score}
                    </span>
                    <DeltaPill delta={selected.delta} />
                  </div>
                  {selected.summary ? <p className="type-body-s text-foundation-base-darkbase-dark-500">{selected.summary}</p> : null}
                </div>
                {selected.resolvedMoment ? (
                  <section className="flex flex-col gap-2.5 rounded-xl bg-white px-4 py-3" data-testid="roleplay-scorecard-moment">
                    <p className="type-body-s-semibold text-foundation-base-darkbase-dark-700">The moment · Turn {selected.moment.learner_turn}</p>
                    <MomentExchange moment={selected.resolvedMoment} persona={persona} personaBubbleClass="bg-foundation-calm-blue-50" />
                  </section>
                ) : null}
                {selected.turn_marks.length ? (
                  <section className="flex flex-col gap-2.5 rounded-xl bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="type-body-s-semibold text-foundation-base-darkbase-dark-700">Across the conversation</p>
                      <MarkLegend />
                    </div>
                    <TurnMarks category={selected} />
                  </section>
                ) : null}
              </div>
              <div className="flex flex-col gap-3">
                <Takeaways category={selected} tinted />
                <Button
                  variant="secondary"
                  onClick={() => practiceSkill(selected)}
                  disabled={practiceDisabled}
                  data-testid="roleplay-scorecard-practice-skill"
                >
                  <RefreshCcw size={16} aria-hidden="true" /> Practice this skill
                </Button>
              </div>
            </div>
          </section>
        </div>
        {conversationDialog}
      </div>
    );
  }

  if (openIndex !== null && view.categories[openIndex]) {
    const selected = view.categories[openIndex];
    const next = view.categories[(openIndex + 1) % count];
    return (
      <div className="rp-scorecard flex w-full max-w-[40rem] flex-col gap-3.5" data-testid="roleplay-results" data-layout="mobile">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="auto" onClick={closeSkill} data-testid="roleplay-scorecard-summary">
            <ArrowLeft size={16} aria-hidden="true" /> Summary
          </Button>
          <div className="flex gap-1.5 pr-2">
            {view.categories.map((category, index) => (
              <button
                key={category.key}
                type="button"
                onClick={() => showSkill(index, { replace: true })}
                aria-label={`Show ${category.label}`}
                aria-current={index === openIndex ? 'true' : undefined}
                className="h-2 rounded-full transition-all"
                style={{
                  width: index === openIndex ? 20 : 8,
                  background: index === openIndex ? 'var(--rp-score-ink)' : 'var(--rp-score-none)',
                }}
              />
            ))}
          </div>
        </div>

        <Card variant="glass" padding="compact" disableAnimation maxWidth="none" data-testid="roleplay-scorecard-detail">
          <div className="flex items-center gap-3.5">
            <ScoreRing score={selected.score} display={selected.score} tone={selected.tone} size={76} thickness={7}>
              <span className="type-heading-h4 text-foundation-base-darkbase-dark-700">{selected.score}</span>
            </ScoreRing>
            <div className="flex min-w-0 flex-col gap-1">
              <h2 ref={detailHeadingRef} tabIndex={-1} className="type-heading-h5 text-foundation-base-darkbase-dark-700 outline-none">
                {selected.label}
              </h2>
              <div className="flex flex-wrap gap-1.5">
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold" style={{ color: SCORE_COLORS[selected.tone] }}>{selected.tier}</span>
                <DeltaPill delta={selected.delta} />
              </div>
            </div>
          </div>
          {selected.summary ? <p className="type-body-s mt-3 text-foundation-base-darkbase-dark-500">{selected.summary}</p> : null}
        </Card>

        {selected.turn_marks.length ? (
          <section className="flex flex-col gap-2.5 rounded-xl bg-white/70 px-4 py-3">
            <p className="type-body-s-semibold text-foundation-base-darkbase-dark-700">Across the conversation</p>
            <TurnMarks category={selected} />
            <MarkLegend showMoment={Boolean(selected.resolvedMoment)} />
          </section>
        ) : null}

        {selected.resolvedMoment ? (
          <section className="flex flex-col gap-2.5 rounded-xl bg-white/70 px-4 py-3" data-testid="roleplay-scorecard-moment">
            <p className="type-body-s-semibold text-foundation-base-darkbase-dark-700">The moment · Turn {selected.moment.learner_turn}</p>
            <MomentExchange moment={selected.resolvedMoment} persona={persona} personaBubbleClass="bg-white" />
          </section>
        ) : null}

        <div className="grid gap-3">
          <Takeaways category={selected} />
        </div>
        <div className="flex flex-col gap-2">
          <Button onClick={() => practiceSkill(selected)} disabled={practiceDisabled} data-testid="roleplay-scorecard-practice-skill">
            <RefreshCcw size={16} aria-hidden="true" /> Practice this skill
          </Button>
          {count > 1 ? (
            <Button variant="secondary" onClick={() => showSkill((openIndex + 1) % count, { replace: true })} data-testid="roleplay-scorecard-next-skill">
              Next: {next.label} <ChevronRight size={16} aria-hidden="true" />
            </Button>
          ) : null}
        </div>
        {conversationDialog}
      </div>
    );
  }

  return (
    <div className="rp-scorecard flex w-full max-w-[40rem] flex-col gap-3.5" data-testid="roleplay-results" data-layout="mobile">
      {onBack ? (
        <div>
          <Button variant="ghost" size="auto" onClick={onBack} data-testid="roleplay-back">
            <ArrowLeft size={16} aria-hidden="true" /> Back
          </Button>
        </div>
      ) : null}
      <div className="px-1">{eyebrow}</div>
      {overallCard}
      {turningPoint}
      <HighlightCards best={best} focus={focus} onOpen={openCategory} />

      <section className="flex flex-col gap-2" aria-label="Your skills">
        <div className="flex items-center justify-between px-1">
          <h2 className="type-body-s-semibold text-foundation-base-darkbase-dark-500">Your skills</h2>
          {view.hasLastTry ? <LastTryLegend /> : null}
        </div>
        {view.categories.map((category, index) => (
          <button
            key={category.key}
            type="button"
            ref={(node) => {
              if (node) skillRowRefs.current.set(category.key, node);
              else skillRowRefs.current.delete(category.key);
            }}
            onClick={() => showSkill(index)}
            className="flex items-center gap-3 rounded-2xl bg-white/70 px-4 py-3.5 text-left shadow-elevation-sm transition-colors hover:bg-white/95"
            data-testid={`roleplay-scorecard-skill-${category.key}`}
            data-score={category.score}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foundation-calm-blue-50 text-foundation-base-darkbase-dark-500">
              <ScorecardIcon name={category.icon} size={18} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className="flex items-baseline justify-between gap-2">
                <span className="flex min-w-0 flex-col">
                  <span className="type-body-s-semibold text-foundation-base-darkbase-dark-700">{category.label}</span>
                  <span className="text-xs font-semibold" style={{ color: SCORE_COLORS[category.tone] }}>{category.tier}</span>
                </span>
                <span className="type-heading-h5 text-foundation-base-darkbase-dark-700">
                  <span aria-hidden="true">{Math.round(category.score * progress)}</span>
                  <span className="sr-only">{category.score} out of 100</span>
                </span>
              </span>
              <ScoreBar category={category} progress={progress} />
            </span>
            <ChevronRight size={18} aria-hidden="true" className="shrink-0 text-foundation-base-darkbase-dark-300" />
          </button>
        ))}
      </section>

      <div className="flex flex-col gap-2">
        {actionButtons}
        {conversationButton('stack')}
      </div>
      {conversationDialog}
    </div>
  );
};
